'use client';

import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { type FormEvent, useCallback, useState } from 'react';
import { isValidPostcode } from '@/lib/util';

import styles from './FormPostcode.module.scss';

export enum ShowOptions {
	all = 'All',
	patients = 'Patients',
	passersby = 'PassersBy',
}

export interface FormPostcodeProps {
	query?: string;
	setQuery: (query: string) => void;
	show: ShowOptions;

	setShow: (show: ShowOptions) => void;

	displayMap: boolean;
	setDisplayMap: (displayMap: boolean) => void;
}

const FormPostcode = ({ query, setQuery, show, setShow, displayMap, setDisplayMap }: FormPostcodeProps) => {
	const t = useTranslations();
	const locale = useLocale();
	const router = useRouter();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [validationError, setValidationError] = useState<string>('');

	const handleShowChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>): void => {
			setShow(e.target.value as ShowOptions);
		},
		[setShow],
	);

	const toggleMap = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>): void => {
			e.preventDefault();
			setDisplayMap(!displayMap);
		},
		[displayMap, setDisplayMap],
	);

	const handleSubmit = useCallback(
		async (e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			setIsSubmitting(true);
			setValidationError('');

			try {
				const formData = new FormData(e.currentTarget);
				const formQueryValue = (formData.get('query') as string)?.trim();
				let url: string;

				if (!formQueryValue || formQueryValue.length < 2) {
					setValidationError('A minimum of 2 characters is');
					return;
				}

				if (isValidPostcode(formQueryValue)) {
					url = `/${locale}/postcode/${formQueryValue}`;
				} else {
					url = `/${locale}/plaats/${formQueryValue}`;
				}

				router.push(url);
				setQuery(formQueryValue);
			} catch (error) {
				console.error('[postcode/form] postcode form submission failed', error);
				setValidationError(t('Generic.an-error-occurred') || 'An error occurred. Please try again.');
			} finally {
				setIsSubmitting(false);
			}
		},
		[locale, router, setQuery, t],
	);

	return (
		<form onSubmit={handleSubmit}>
			{validationError && (
				<div className="error-message" style={{ color: 'red', marginBottom: '10px', fontSize: '14px' }}>
					{validationError}
				</div>
			)}
			<div className="flex-row" style={{ gap: '10px' }}>
				<div className="col-6">
					<h2>
						<label htmlFor="query-input">{t('Generic.zoek-huisarts')}</label>
					</h2>
					<div className="flex-row" style={{ gap: '10px' }}>
						<input
							id="query-input"
							type="text"
							name="query"
							defaultValue={query}
							minLength={2}
							maxLength={29}
							required
							disabled={isSubmitting}
							placeholder="1234AB"
							style={{ flexGrow: 1 }}
							aria-describedby={validationError ? 'query-error' : undefined}
						/>
						<button type="submit" className="primary" style={{ flexGrow: 0 }} disabled={isSubmitting}>
							{isSubmitting ? t('Generic.loading') || 'Loading...' : t('Generic.Zoek')}
						</button>
					</div>
					{validationError && (
						<div id="query-error" role="alert" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
							{validationError}
						</div>
					)}
				</div>
				<div className={clsx('col-6', styles.showLegend)}>
					{/* biome-ignore lint/a11y/noLabelWithoutControl: label visually groups the select below; restructuring the form is out of scope */}
					<label>{t('Generic.Toon')}</label>
					<div className="flex-row" style={{ gap: '10px' }}>
						<select onChange={handleShowChange} defaultValue={show} style={{ flexGrow: 1 }}>
							<option value={ShowOptions.all}>{t('Generic.Alle')}</option>
							<option value={ShowOptions.patients}>{t('Generic.Legend.InschrijvenMogelijk')}</option>
							<option value={ShowOptions.passersby}>{t('Generic.Legend.PassantenWelkom')}</option>
						</select>

						<button
							type="button"
							onClick={toggleMap}
							style={{ flexGrow: 0, display: 'flex' }}
							className={displayMap ? 'primary' : ''}
						>
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src="/icons/map.svg" alt={`icon`} />
							{t('Generic.Kaart')}
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};
export default FormPostcode;
